import { css } from '@emotion/react';
import {
  faCog,
  faExchange, faExternalLink,
  faEye,
  faEyeSlash,
  faPencil,
  faSearchLocation,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC,
  type Ref,
} from 'react';
import {
  ContextMenuComponent, ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { darkTheme } from '~/_shared/themes/dark.theme';
import { lightTheme } from '~/_shared/themes/light.theme';
import type { Theme } from '~/_shared/themes/theme.model';
import { getDarkOrLightDependingOnBaseColor } from '~/_shared/utils/colors/colors.helpers';
import {
  createColor,
  rgbColorToString,
} from '../../_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '../../_shared/components/colorPicker/colorPicker.types';
import { OverlayLoaderComponent } from '../../_shared/components/overlay/overlayLoader.component';
import { type UnitSystem } from '../../_shared/types/googleMaps/googleMaps.types';
import { stopPropagationAnd } from '../../_shared/utils/dom/dom.helpers';
import { useTranslation } from '../../_shared/utils/hooks';
import { type MapSettingsRoute } from '../../store/mapSettings/directions/mapSettingsDirections.state';
import { getDefaultOrCustomRouteName } from '../panel/directionsPanel.helpers';
import {
  getDistanceLabel,
  getDurationLabel,
} from './directionsListing.helpers';

const headingWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
});

const colorTileStyle = (color: ColorResult) => css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 10,
  width: 22,
  height: 22,
  borderRadius: 4,
  background: rgbColorToString(color.rgba),
  flexShrink: 0,
});

const settingsButtonStyle = (theme: Theme) => css({
  background: 'none',
  border: 'none',
  fontSize: '16px',
  color: theme.textColors.primary,

  '@media print': {
    display: 'none',
  },
});

const settingsButtonWrapperStyle = css({
  marginLeft: 'auto',
});

const routeInfoStyle = css({
  fontSize: '12px',
  fontWeight: 400,
  margin: 0,
  flex: 1,
  overflow: 'hidden',
  lineHeight: 1.15,
});

const routeEstimationStyle = css({
  margin: 0,
});

const routeNameStyle = css({
  textTransform: 'uppercase',
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const stepsCountStyle = css({
  textTransform: 'uppercase',
  margin: 0,
});

const visibilityIconStyle = css({
  textAlign: 'center',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  margin: 0,
});

type RouteHeaderProps = Readonly<{
  isLoading: boolean;
  isMenuVisible: boolean;
  isVisible: boolean;
  routeItem: MapSettingsRoute;
  totalDistance: number;
  totalDuration: number;
  unitSystem: UnitSystem;

  onEditRoute?: () => void;
  onHideMenu?: () => void;
  onHideRoute: () => void;
  onLoadDirections?: () => void;
  onRemoveRoute?: () => void;
  onRouteExport?: () => void;
  onShowMenu?: () => void;
  onShowRoute: () => void;
  onZoomToRoute?: () => void;
}>;

export const RouteHeaderComponent: FC<RouteHeaderProps> = props => {
  const [t] = useTranslation();
  const routeColor = createColor(props.routeItem.color);
  const darkIconColor = createColor(darkTheme.iconColors.backgroundPrimary);
  const darkIconColorHover = createColor(darkTheme.iconColors.backgroundPrimaryHover);
  const lightIconColor = createColor(lightTheme.iconColors.backgroundPrimary);
  const lightIconColorHover = createColor(lightTheme.iconColors.backgroundPrimaryHover);

  return (
    <div css={headingWrapperStyle}>
      <span
        css={colorTileStyle(routeColor)}
      >
        <HoverableIconComponent
          css={visibilityIconStyle}
          icon={props.isVisible ? faEye : faEyeSlash}
          baseColor={getDarkOrLightDependingOnBaseColor(routeColor, darkIconColor, lightIconColor).hex}
          hoverColor={getDarkOrLightDependingOnBaseColor(routeColor, darkIconColorHover, lightIconColorHover).hex}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault(); // without this accordion's toggle event would trigger
            if (props.isVisible) {
              props.onHideRoute();
            }
            else {
              props.onShowRoute();
            }
          }}
        />
      </span>

      <div css={routeInfoStyle}>
        <p css={routeNameStyle}>{getDefaultOrCustomRouteName(props.routeItem.name, t)}</p>
        <p css={stepsCountStyle}>
          ({t('{{count}} Stops', { count: props.routeItem.waypoints.length - 1 })})
        </p>
        <p css={routeEstimationStyle}>
          {getDistanceLabel(props.totalDistance, props.unitSystem)} — {getDurationLabel(props.totalDuration, t)}
        </p>
      </div>

      <div
        css={settingsButtonWrapperStyle}
        onClick={e => e.stopPropagation()}
      >
        <ContextMenuComponent
          isFixed
          renderMenuContent={() => (
            <>
              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  icon={faExchange}
                  onClick={props.onLoadDirections}
                >
                  {t('Edit Route')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  icon={faPencil}
                  onClick={props.onEditRoute}
                >
                  {t('Route Settings')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
              {props.onRouteExport && (
                <ContextMenuItemCoreComponent>
                  <ContextMenuDefaultItem
                    icon={faExternalLink}
                    onClick={props.onRouteExport}
                  >
                    {t('Export Data')}
                  </ContextMenuDefaultItem>
                </ContextMenuItemCoreComponent>
              )}
              <ContextMenuItemCoreComponent>
                <ContextMenuDefaultItem
                  icon={faSearchLocation}
                  onClick={props.onZoomToRoute}
                >
                  {t('Zoom to Route')}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
              <ContextMenuItemCoreComponent>
                {props.onRemoveRoute && (
                  <ContextMenuDefaultItem
                    icon={faTrash}
                    isDestructive
                    onClick={props.onRemoveRoute}
                  >
                    {t('Delete Route')}
                  </ContextMenuDefaultItem>
                )}
              </ContextMenuItemCoreComponent>
            </>
          )}
          isVisible={props.isMenuVisible}
          onHide={props.onHideMenu}
          preferredPlacement="bottom-end"
        >
          {ref => (
            <button
              ref={ref as Ref<HTMLButtonElement>}
              css={settingsButtonStyle}
              onClick={stopPropagationAnd(props.onShowMenu)}
            >
              <FontAwesomeIcon icon={faCog} />
            </button>
          )}
        </ContextMenuComponent>
      </div>

      {props.isLoading && (
        <OverlayLoaderComponent loaderSize={22} />
      )}
    </div>
  );
};
